import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import Chart from "react-apexcharts";
import { fetchIncomeStatementData } from "../../apis/Stock";
import useDarkMode from "../../hooks/useDarkMode";
import { getChartTheme } from "../../utils/chartTheme";

const EPS_COLORS = ["#0f6fe0", "#dd041e"];

const buildPeriodLabel = (value, period) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value ?? "");

  if (period === "annual") {
    return String(date.getFullYear());
  }

  const year = String(date.getFullYear()).slice(2);
  const quarter = Math.ceil((date.getMonth() + 1) / 3);
  return `${year}Q${quarter}`;
};

const toNumberOrNull = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export default function EPSLineChart({ ticker, selectedPeriod = "quarter" }) {
  const [chartDataByPeriod, setChartDataByPeriod] = useState({ quarter: null, annual: null });
  const [isLoading, setIsLoading] = useState(false);
  const isDarkMode = useDarkMode();
  const chartColors = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);

  useEffect(() => {
    if (!ticker) {
      setChartDataByPeriod({ quarter: null, annual: null });
      setIsLoading(false);
      return;
    }

    setChartDataByPeriod({ quarter: null, annual: null });
  }, [ticker]);

  useEffect(() => {
    if (!ticker) {
      return;
    }

    if (chartDataByPeriod[selectedPeriod] !== null) {
      return;
    }

    let isMounted = true;

    async function fetchData() {
      setIsLoading(true);

      try {
        const response = await fetchIncomeStatementData(ticker, selectedPeriod);

        if (!response.ok) {
          throw new Error("Failed to fetch income statement");
        }

        const data = await response.json();
        if (!isMounted) return;

        setChartDataByPeriod((prev) => ({
          ...prev,
          [selectedPeriod]: Array.isArray(data) ? data : [],
        }));
      } catch (error) {
        console.error("Error fetching income statement:", error);
        if (isMounted) {
          setChartDataByPeriod((prev) => ({
            ...prev,
            [selectedPeriod]: [],
          }));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [chartDataByPeriod, selectedPeriod, ticker]);

  const rows = useMemo(
    () => (Array.isArray(chartDataByPeriod[selectedPeriod]) ? [...chartDataByPeriod[selectedPeriod]] : [])
      .filter((item) => item?.date)
      .filter((item) => toNumberOrNull(item?.eps) !== null || toNumberOrNull(item?.epsDiluted) !== null)
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [chartDataByPeriod, selectedPeriod]
  );

  const categories = useMemo(
    () => rows.map((item, index) => {
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return buildPeriodLabel(item.date, selectedPeriod);

      const currentYear = date.getFullYear();
      if (selectedPeriod === "annual") {
        return String(currentYear);
      }

      const previousRow = index > 0 ? rows[index - 1] : null;
      const previousYear = previousRow?.date ? new Date(previousRow.date).getFullYear() : null;
      return currentYear !== previousYear ? String(currentYear) : "";
    }),
    [rows, selectedPeriod]
  );

  const series = useMemo(
    () => [
      {
        name: "EPS",
        data: rows.map((item) => toNumberOrNull(item?.eps)),
      },
      {
        name: "희석 EPS",
        data: rows.map((item) => toNumberOrNull(item?.epsDiluted)),
      },
    ],
    [rows]
  );

  const hasData = useMemo(
    () => series.some((entry) => entry.data.some((value) => value !== null)),
    [series]
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: false },
        selection: { enabled: false },
        animations: { enabled: false },
        background: chartColors.panelBg,
      },
      colors: EPS_COLORS,
      stroke: {
        curve: "straight",
        width: 2.5,
      },
      markers: {
        size: 3,
        strokeWidth: 0,
        strokeColors: "transparent",
        fillOpacity: 1,
        hover: {
          size: 4,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        labels: {
          colors: chartColors.legendColor,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 0,
        },
      },
      title: {
        text: "주당순이익(EPS)",
        align: "center",
        offsetY: 10,
        style: {
          color: chartColors.legendColor,
          fontSize: "18px",
          fontWeight: 700,
        },
      },
      grid: {
        borderColor: chartColors.gridColor,
        strokeDashArray: 4,
        padding: {
          top: 2,
          bottom: 2,
        },
      },
      xaxis: {
        categories,
        labels: {
          style: {
            colors: chartColors.tickColor,
            fontSize: "11px",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: {
            colors: chartColors.tickColor,
            fontSize: "11px",
          },
          formatter: (value) => {
            const numeric = Number(value);
            return Number.isFinite(numeric) ? numeric.toFixed(2).replace(/\.00$/, "") : "-";
          },
        },
      },
      tooltip: {
        theme: isDarkMode ? "dark" : "light",
        shared: true,
        intersect: false,
        x: {
          formatter: (_, context) => rows?.[context.dataPointIndex]?.date || "",
        },
        y: {
          formatter: (value) => {
            const numeric = Number(value);
            return Number.isFinite(numeric) ? numeric.toFixed(3).replace(/0+$/, "").replace(/\.$/, "") : "-";
          },
        },
      },
      noData: {
        text: "데이터가 없습니다.",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: chartColors.tickColor,
        },
      },
    }),
    [categories, chartColors, isDarkMode, rows]
  );

  if (isLoading) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center h-100"
        style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderRadius: "12px", color: chartColors.tickColor }}
      >
        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ fontSize: "13px" }}>데이터를 불러오는 중입니다...</div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div
        className="d-flex align-items-center justify-content-center h-100"
        style={{ border: `1px dashed ${chartColors.panelBorder}`, minHeight: "300px", backgroundColor: chartColors.panelBg }}
      >
        데이터가 없습니다.
      </div>
    );
  }

  return (
    <Card
      className="w-full h-full"
      style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderColor: chartColors.panelBorder }}
    >
      <Chart options={options} series={series} type="line" height={300} />
    </Card>
  );
}
